/**
 * La FICHA de una fila de la conciliación: lo que se abre al pulsarla.
 *
 * Un solo panel para todo lo que puede ser una fila —un gasto apuntado, un movimiento del
 * banco, un cobro fuera del banco— con TODOS sus datos a mano: lo que se puede cambiar, en un
 * campo; lo que no (lo que dice el banco, lo que fija el contrato), a la vista y sin campo.
 *
 * BORRAR AQUÍ ES VACIAR UN DATO, no la fila: cada texto lleva su «×» y el documento su «Quitar».
 * Borrar el apunte o el cobro entero está en el menú «⋯» de la fila, con confirmación, lejos de
 * «Guardar».
 *
 * Los datos se guardan juntos con «Guardar». El documento, en cambio, va en el momento: subirlo
 * o quitarlo no espera al botón, igual que en el resto de la aplicación.
 */
import { useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BankMovement } from '@naar/shared';
import { GASTOS_GENERALES_ID, GASTOS_GENERALES_NOMBRE, repartirAPartesIguales } from '@naar/shared';
import { ApiError, api, queryKeys } from '../../api/client';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Drawer } from '../../components/overlay/Drawer';
import { useDocumentViewer } from '../../components/overlay/DocumentViewer';
import { Checkbox, Field, FormRow, FormSection, Input, MoneyInput, Select, Textarea } from '../../components/form/Field';
import { toast } from '../../components/feedback/Toaster';
import { formatEurCents, formatShortDate } from '../../lib/format';
import { avisarReapertura } from './MissingPanel';
import { esCargoPendiente, subirDocumentoDeFila } from './RowEditor';
import { REPARTIDO_ID, aPropertyId, type EstadoFila, type FilaConciliacion } from './reconciliation';
import ledger from './Ledger.module.css';
import styles from './FilaDrawer.module.css';

const ESTADO: Record<EstadoFila, string> = {
  completo: 'Completo',
  sin_justificar: 'Completo sin justificar',
  pendiente: 'Pendiente',
};

const ESTADO_BANCO: Record<BankMovement['status'], string> = {
  identificado: 'Identificado: es de un inquilino',
  asignado: 'Asignado a un inmueble',
  enlazado: 'Enlazado con un gasto apuntado',
  ignorado: 'Ignorado: no es de ningún inmueble',
  pendiente: 'Pendiente: nadie ha dicho todavía qué es',
};

function euros(value: number | null): string {
  if (value === null) return '—';
  return value < 0 ? `-${formatEurCents(Math.abs(value))}` : formatEurCents(value);
}

interface FilaDrawerProps {
  fila: FilaConciliacion;
  /** El mes de la fila: para avisar si añadir un papel ha reabierto un mes conciliado. */
  month: string;
  properties: { id: string; name: string }[];
  /** Los inquilinos a los que se puede imputar un ingreso («Quién paga»). */
  tenants?: {
    id: string;
    fullName: string;
    label: string;
    propertyId: string | null;
    propertyName: string | null;
    unitCode: string | null;
  }[];
  onClose: () => void;
  /** Abre el panel de «Resolver» de un movimiento del banco (de quién es, a qué gasto paga…). */
  onResolve?: (movement: BankMovement) => void;
}

export function FilaDrawer({ fila, month, properties, tenants = [], onClose, onResolve }: FilaDrawerProps) {
  const queryClient = useQueryClient();
  const visor = useDocumentViewer();
  const ficheroRef = useRef<HTMLInputElement>(null);

  const entry = fila.entry?.entry;
  const movement = fila.movement;
  const payment = fila.payment;
  const cargoPendiente = esCargoPendiente(fila);

  // --- Lo que se puede cambiar, con su valor de partida ---------------------------
  const inicial = {
    concept: entry?.concept ?? '',
    amount: entry?.amount ?? '',
    issuedAt: entry?.issuedAt ?? '',
    vendor: entry ? (entry.vendorName ?? '') : (movement?.vendorName ?? ''),
    inmueble: fila.inmueble?.id ?? '',
    habitacion: entry?.unit?.id ?? movement?.unit?.id ?? '',
    reparto: (fila.reparto ?? []).map((parte) => parte.propertyId),
    notes: entry ? (entry.notes ?? '') : movement ? (movement.notes ?? '') : (payment?.reference ?? ''),
    // Lo GUARDADO. Lo que propone la IA viene elegido abajo, pero cuenta como un cambio: no está guardado.
    pagador: fila.pagador && !fila.pagador.sugerido ? fila.pagador.id : '',
  };
  const [concept, setConcept] = useState(inicial.concept);
  const [amount, setAmount] = useState(inicial.amount);
  const [issuedAt, setIssuedAt] = useState(inicial.issuedAt);
  const [vendor, setVendor] = useState(inicial.vendor);
  /*
   * LA PROPUESTA DE LA IA de un pendiente viene ya puesta en la ficha (el inmueble, quién paga) y,
   * si no se cambia, «Guardar» la aplica TAL CUAL: con su concepto, su gasto fijo o su gasto
   * general, y —si es un cobro— también en los ingresos iguales.
   */
  const ia = movement?.status === 'pendiente' ? movement.aiProposal : null;
  const iaInmueble =
    ia && (ia.action === 'property' || ia.action === 'recurring')
      ? ia.propertyId
      : ia?.action === 'general'
        ? GASTOS_GENERALES_ID
        : null;
  const [inmueble, setInmueble] = useState(inicial.inmueble || iaInmueble || '');
  const [habitacion, setHabitacion] = useState(inicial.habitacion);
  const [reparto, setReparto] = useState<string[]>(inicial.reparto);
  const [notes, setNotes] = useState(inicial.notes);
  const [pagador, setPagador] = useState(fila.pagador?.id ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const editaGasto = Boolean(entry);
  // Un gasto apuntado o un CARGO del banco pueden ser GASTOS GENERALES (de ningún inmueble); un ingreso, no.
  const opcionesInmueble = [
    ...properties,
    ...(entry || movement?.kind === 'gasto' ? [{ id: GASTOS_GENERALES_ID, name: GASTOS_GENERALES_NOMBRE }] : []),
    // Un CARGO del banco de varios pisos se reparte a partes iguales (la comunidad de dos pisos).
    ...(movement?.kind === 'gasto' ? [{ id: REPARTIDO_ID, name: 'Varios pisos, a partes iguales…' }] : []),
  ];
  const repartiendo = inmueble === REPARTIDO_ID;
  // La habitación, solo en un gasto de UN piso (una regularización de suministros, por ejemplo).
  const pisoUnico = inmueble && inmueble !== GASTOS_GENERALES_ID && !repartiendo ? inmueble : null;
  const editaHabitacion = Boolean(pisoUnico && (entry || movement?.kind === 'gasto'));
  const detallePiso = useQuery({
    queryKey: queryKeys.property(pisoUnico ?? ''),
    queryFn: () => api.property(pisoUnico!),
    enabled: editaHabitacion,
    staleTime: 5 * 60_000,
  });
  const habitaciones = detallePiso.data?.units ?? [];
  /** Los pisos que se pueden repartir: los de verdad, en el orden de la lista. */
  const pisosReparto = properties.filter((p) => p.id !== GASTOS_GENERALES_ID);
  const partesReparto =
    repartiendo && movement && reparto.length >= 2 ? repartirAPartesIguales(movement.amount, reparto.length) : [];
  const editaProveedor = Boolean(entry || movement);
  const editaInmueble = Boolean(entry || movement);
  const editaNotas = Boolean(entry || movement || payment);
  const editaPagador = !entry && movement?.kind === 'ingreso';

  const cambios = {
    concept: editaGasto && concept.trim() !== inicial.concept,
    amount: editaGasto && Number(amount) !== Number(inicial.amount),
    issuedAt: editaGasto && issuedAt !== inicial.issuedAt,
    vendor: editaProveedor && vendor.trim() !== inicial.vendor,
    inmueble: editaInmueble && Boolean(inmueble) && !repartiendo && inmueble !== inicial.inmueble,
    habitacion: editaHabitacion && habitacion !== inicial.habitacion,
    reparto: repartiendo && [...reparto].sort().join() !== [...inicial.reparto].sort().join(),
    notes: editaNotas && notes.trim() !== inicial.notes,
    pagador: editaPagador && Boolean(pagador) && pagador !== inicial.pagador,
  };
  /*
   * ¿Se aplica la propuesta de la IA tal cual? Sí mientras lo que propone siga elegido: el mismo
   * inquilino o el mismo inmueble. Un apunte que enlazar o ignorarlo no tienen campo: se aplican.
   */
  const aceptaIa =
    Boolean(ia) &&
    (ia!.action === 'tenant'
      ? pagador === ia!.tenantId
      : iaInmueble
        ? inmueble === iaInmueble && !cambios.pagador && !cambios.habitacion && !repartiendo
        : !cambios.pagador && !cambios.inmueble && !cambios.habitacion && !repartiendo);
  /** Un «Cambio IA» o un «Aviso IA» se dan por buenos guardando sin tocar nada: se quita la marca. */
  const daPorBuenoCambio = Boolean(movement && (fila.cambioIa || fila.avisoIa));
  const hayCambios = Object.values(cambios).some(Boolean) || aceptaIa || daPorBuenoCambio;
  /** Lo que propone la IA en su aviso sobre algo ya resuelto. */
  const propuestaAviso = movement && fila.avisoIa ? movement.aiProposal : null;
  /** El inquilino elegido como quien paga, con su piso y su habitación. */
  const pagadorElegido = tenants.find((t) => t.id === pagador) ?? null;
  const sinRevisar = entry?.status === 'borrador';

  function fallo(error: unknown, generico: string) {
    if (error instanceof ApiError) {
      const errores: Record<string, string> = {};
      for (const detalle of error.fieldErrors) errores[detalle.path] = detalle.message;
      setFieldErrors(errores);
      if (Object.keys(errores).length === 0) toast.error(error.message);
      return;
    }
    toast.error(generico);
  }

  // --- Guardar los datos ------------------------------------------------------------
  const guardar = useMutation({
    mutationFn: async (confirmar: boolean) => {
      /*
       * REPARTIR (o cambiar los pisos de un reparto) y DESHACER un reparto van por el cargo del
       * banco, no por el apunte: crean o quitan un apunte por piso.
       */
      if (movement && repartiendo && cambios.reparto) {
        if (reparto.length < 2) throw new Error('reparto');
        const body: { propertyIds: string[]; vendorName?: string | null; notes?: string | null } = {
          propertyIds: reparto,
        };
        if (cambios.vendor) body.vendorName = vendor.trim() || null;
        if (cambios.notes) body.notes = notes.trim() || null;
        return (await api.updateMovementDetails(movement.id, body)).alsoChanged;
      }
      if (movement && fila.reparto && cambios.inmueble) {
        return (
          await api.updateMovementDetails(movement.id, {
            propertyId: inmueble,
            ...(habitacion && editaHabitacion ? { unitId: habitacion } : {}),
            ...(cambios.notes ? { notes: notes.trim() || null } : {}),
          })
        ).alsoChanged;
      }
      if (entry) {
        const body: Record<string, unknown> = {};
        if (cambios.concept) body.concept = concept.trim();
        if (cambios.amount) body.amount = amount;
        if (cambios.issuedAt) body.issuedAt = issuedAt;
        if (cambios.vendor) body.vendorName = vendor.trim() || null;
        if (cambios.inmueble) body.propertyId = aPropertyId(inmueble);
        if (cambios.habitacion) body.unitId = habitacion || null;
        if (cambios.notes) body.notes = notes.trim() || null;
        if (confirmar) body.status = 'confirmado';
        if (Object.keys(body).length > 0) await api.updateLedgerEntry(entry.id, body);
        // El cargo de este gasto lo cambió la IA: guardar es darlo por revisado.
        if (movement && daPorBuenoCambio) await api.updateMovementDetails(movement.id, {});
        return 0;
      }
      if (movement) {
        const body: {
          vendorName?: string | null;
          notes?: string | null;
          propertyId?: string;
          unitId?: string | null;
          tenantId?: string;
          applyToSame?: boolean;
          acceptAiProposal?: boolean;
        } = {};
        if (cambios.vendor) body.vendorName = vendor.trim() || null;
        if (cambios.notes) body.notes = notes.trim() || null;
        if (aceptaIa) {
          // Lo que propuso la IA, tal cual: su concepto, su gasto fijo, su gasto general…
          Object.assign(body, { acceptAiProposal: true });
        } else {
          if (cambios.inmueble) body.propertyId = inmueble;
          if (cambios.habitacion) body.unitId = habitacion || null;
          // Quién paga, y lo mismo en los ingresos exactamente iguales (concepto e importe).
          if (cambios.pagador) Object.assign(body, { tenantId: pagador, applyToSame: true });
        }
        if (Object.keys(body).length > 0 || daPorBuenoCambio) {
          return (await api.updateMovementDetails(movement.id, body)).alsoChanged;
        }
        return 0;
      }
      if (payment && cambios.notes) await api.updatePaymentNotes(payment.id, notes.trim() || null);
      return 0;
    },
    onSuccess: async (iguales, confirmar) => {
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      await queryClient.invalidateQueries({ queryKey: ['utilities'] });
      toast.success(
        confirmar
          ? 'Apunte confirmado.'
          : iguales && iguales > 0
            ? `Cambios guardados. Quién paga también en ${iguales === 1 ? '1 movimiento igual' : `${iguales} movimientos iguales`}.`
            : 'Cambios guardados.',
      );
      onClose();
    },
    onError: (error) => {
      if (error instanceof Error && error.message === 'reparto') {
        toast.error('Para repartir el cargo, marca al menos dos pisos.');
        return;
      }
      fallo(error, 'No se han podido guardar los cambios.');
    },
  });

  // --- El documento: va en el momento ---------------------------------------------------
  const subir = useMutation({
    mutationFn: (file: File) => subirDocumentoDeFila(fila, file, inmueble),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      toast.success('Documento guardado.');
      if (result.reopened) avisarReapertura(month);
    },
    onError: (error) => {
      if (error instanceof Error && error.message === 'inmueble') {
        toast.error('Elige primero de qué inmueble es el gasto.');
        return;
      }
      fallo(error, 'No se ha podido subir el documento.');
    },
  });

  const quitar = useMutation({
    mutationFn: async () => {
      if (entry) return api.updateLedgerEntry(entry.id, { documentId: null });
      if (movement) return api.removeMovementDocument(movement.id);
      if (payment) return api.removePaymentDocument(payment.id);
      return undefined;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      toast.success('Documento quitado. Sigue guardado en Documentos.');
    },
    onError: (error) => fallo(error, 'No se ha podido quitar el documento.'),
  });

  // --- El aviso de la IA: aplicar lo que propone, en vez de lo que hay ---------------------
  const aplicarAviso = useMutation({
    mutationFn: () => api.updateMovementDetails(movement!.id, { acceptAiProposal: true }),
    onSuccess: async ({ alsoChanged }) => {
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      await queryClient.invalidateQueries({ queryKey: ['utilities'] });
      toast.success(
        alsoChanged > 0
          ? `Aplicado lo que propone la IA. Quién paga también en ${alsoChanged === 1 ? '1 movimiento igual' : `${alsoChanged} movimientos iguales`}.`
          : 'Aplicado lo que propone la IA.',
      );
      onClose();
    },
    onError: (error) => fallo(error, 'No se ha podido aplicar lo que propone la IA.'),
  });

  const ocupado = guardar.isPending || subir.isPending || quitar.isPending || aplicarAviso.isPending;
  const documento = fila.soporte && fila.soporte !== 'extracto' ? fila.soporte : null;
  const valido = !editaGasto || Boolean(concept.trim() && amount && issuedAt);

  const titulo = entry ? 'Gasto apuntado' : movement ? 'Movimiento del banco' : 'Cobro fuera del banco';

  return (
    <Drawer
      open
      title={titulo}
      subtitle={[fila.concepto, fila.fecha ? formatShortDate(fila.fecha) : null].filter(Boolean).join(' · ')}
      onClose={onClose}
      dirty={hayCambios}
      footer={
        <div className={ledger.pie}>
          <span />
          <div className={ledger.pieDerecha}>
            <Button variant="secondary" size="sm" onClick={onClose} disabled={guardar.isPending}>
              {hayCambios ? 'Cancelar' : 'Cerrar'}
            </Button>
            {sinRevisar ? (
              <Button size="sm" onClick={() => guardar.mutate(true)} disabled={!valido || ocupado}>
                {guardar.isPending ? 'Guardando…' : 'Confirmar'}
              </Button>
            ) : (
              <Button size="sm" onClick={() => guardar.mutate(false)} disabled={!hayCambios || !valido || ocupado}>
                {guardar.isPending ? 'Guardando…' : 'Guardar'}
              </Button>
            )}
          </div>
        </div>
      }
    >
      {/* --- Resumen: importe y estado, de un vistazo --------------------------------- */}
      <div className={styles.resumen}>
        <div>
          <span className={styles.resumenEtiqueta}>Importe</span>
          <strong className={fila.importe !== null && fila.importe > 0 ? `${styles.importe} ${styles.ingreso}` : styles.importe}>
            {euros(fila.importe)}
          </strong>
        </div>
        <span className={`${styles.estado} ${styles[`estado_${fila.estado}`]}`}>
          <span className={styles.punto} aria-hidden="true" />
          {ESTADO[fila.estado]}
        </span>
      </div>

      {sinRevisar && entry?.guessedFrom && (
        <p className={styles.aviso}>
          <Icon name="info" size={15} strokeWidth={2} />
          <span>
            Sin revisar: lo rellenó la aplicación ({entry.guessedFrom}). Compáralo con la factura y pulsa
            «Confirmar».
          </span>
        </p>
      )}

      {/* --- Lo que cambió la IA de algo ya resuelto: se avisa hasta que una persona lo toca -- */}
      {fila.cambioIa && (
        <div className={ledger.iaDuda} role="note">
          <span className={ledger.iaDudaTag}>Cambio de la IA</span>
          <p>{fila.cambioIa}</p>
          <p className={ledger.iaPropone}>
            Si está bien, pulsa «Guardar» y se quita el aviso. Si no, corrígelo aquí o en «Resolver este
            movimiento…».
          </p>
        </div>
      )}

      {/* --- La IA cree que algo ya resuelto está mal, pero no lo ha cambiado ------------- */}
      {fila.avisoIa && (
        <div className={ledger.iaDuda} role="note">
          <span className={ledger.iaDudaTag}>Aviso de la IA</span>
          <p>{fila.avisoIa}</p>
          <p className={ledger.iaPropone}>
            {propuestaAviso
              ? `Propone: ${queHaraLaIa(propuestaAviso, tenants, properties)}. `
              : ''}
            Si está bien como está, pulsa «Guardar» y se quita el aviso.
          </p>
          {propuestaAviso && (
            <Button variant="secondary" size="sm" onClick={() => aplicarAviso.mutate()} disabled={ocupado}>
              {aplicarAviso.isPending ? 'Aplicando…' : 'Aplicar lo que propone'}
            </Button>
          )}
        </div>
      )}

      {/* --- La duda de la IA y su propuesta: «Guardar» la aplica si no se cambia ---------- */}
      {fila.duda && (
        <div className={ledger.iaDuda} role="note">
          <span className={ledger.iaDudaTag}>Duda de la IA</span>
          <p>{fila.duda}</p>
          {movement && !entry && (
            <p className={ledger.iaPropone}>
              {ia
                ? aceptaIa
                  ? `Si te parece bien, pulsa «Guardar» y se aplica lo que propone: ${queHaraLaIa(ia, tenants, properties)}.`
                  : 'Has cambiado lo que proponía: al guardar se aplica lo que has elegido.'
                : 'Resuélvela aquí (quién paga, inmueble) o en «Resolver este movimiento…», abajo.'}
            </p>
          )}
        </div>
      )}

      {/* --- El gasto (solo los apuntes): concepto, importe y fecha -------------------- */}
      {entry ? (
        <FormSection title="El gasto">
          <Field label="Concepto" required error={fieldErrors.concept}>
            <Input
              value={concept}
              onChange={(event) => setConcept(event.target.value)}
              maxLength={160}
              invalid={Boolean(fieldErrors.concept)}
            />
          </Field>
          <FormRow>
            <Field label="Importe" required error={fieldErrors.amount}>
              <MoneyInput
                value={amount}
                onValueChange={setAmount}
                onChange={() => undefined}
                invalid={Boolean(fieldErrors.amount)}
              />
            </Field>
            <Field label="Fecha" required help="Dentro del mes que estás cuadrando." error={fieldErrors.issuedAt}>
              <Input
                type="date"
                value={issuedAt}
                onChange={(event) => setIssuedAt(event.target.value)}
                invalid={Boolean(fieldErrors.issuedAt)}
              />
            </Field>
          </FormRow>
          {movement && (
            <dl className={styles.datos}>
              <Dato etiqueta="Cargo del banco">
                {movement.description} · {formatShortDate(movement.bookedAt)}
              </Dato>
            </dl>
          )}
        </FormSection>
      ) : (
        <FormSection title={movement ? 'Lo que dice el banco' : 'El cobro'}>
          <dl className={styles.datos}>
            <Dato etiqueta="Fecha">{fila.fecha ? formatShortDate(fila.fecha) : '—'}</Dato>
            <Dato etiqueta="Concepto">{fila.concepto}</Dato>
            {payment && <Dato etiqueta="Inquilino">{fila.proveedor ?? '—'}</Dato>}
            {payment && <Dato etiqueta="Inmueble">{fila.inmueble?.name ?? '—'}</Dato>}
          </dl>
          {payment && <p className={styles.nota}>El inquilino y el inmueble los marca el contrato.</p>}
        </FormSection>
      )}

      {/* --- Proveedor, inmueble y observaciones --------------------------------------- */}
      <FormSection title="Datos">
        {editaProveedor && (
          <Field label="Proveedor" optional error={fieldErrors.vendorName}>
            <ConVaciar valor={vendor} onVaciar={() => setVendor('')} etiqueta="Vaciar el proveedor">
              <Input
                value={vendor}
                onChange={(event) => setVendor(event.target.value)}
                maxLength={120}
                placeholder={!entry && fila.proveedor ? fila.proveedor : 'Iberdrola, Comunidad…'}
              />
            </ConVaciar>
          </Field>
        )}
        {editaInmueble && (
          <Field
            label="Inmueble"
            help={
              cargoPendiente && !inicial.inmueble && !repartiendo
                ? 'Al elegirlo, el cargo queda apuntado como gasto de ese inmueble.'
                : undefined
            }
            error={fieldErrors.propertyId}
          >
            <div className={styles.conPestana}>
              <Select
                options={opcionesInmueble.map((p) => ({ value: p.id, label: p.name }))}
                placeholder={inicial.inmueble ? undefined : 'Sin inmueble'}
                value={inmueble}
                onChange={(event) => {
                  const elegido = event.target.value;
                  // La habitación es de su piso: al cambiar de piso, ninguna (o la que ya tenía).
                  setHabitacion(elegido === inicial.inmueble ? inicial.habitacion : '');
                  // Al pasar a «Varios pisos», el piso que ya tenía viene marcado.
                  if (elegido === REPARTIDO_ID && reparto.length === 0 && pisoUnico) setReparto([pisoUnico]);
                  setInmueble(elegido);
                }}
              />
              {/* La habitación de quien paga, junto a su inmueble. */}
              {editaPagador && pagadorElegido?.unitCode && pagadorElegido.propertyId === inmueble && (
                <span className={styles.pestana} title={`Habitación de ${pagadorElegido.fullName}`}>
                  {pagadorElegido.unitCode}
                </span>
              )}
            </div>
          </Field>
        )}
        {editaHabitacion && habitaciones.length > 0 && (
          <Field
            label="Habitación"
            optional
            help="Solo si el gasto es de una habitación, como la regularización de suministros que se le devuelve a quien vive en ella."
            error={fieldErrors.unitId}
          >
            <Select
              options={habitaciones.map((unidad) => ({ value: unidad.id, label: unidad.code }))}
              placeholder="Todo el piso"
              value={habitacion}
              onChange={(event) => setHabitacion(event.target.value)}
            />
          </Field>
        )}
        {repartiendo && movement && (
          <Field
            label="Pisos del reparto"
            help={
              partesReparto.length > 0
                ? `${formatEurCents(Math.abs(Number(movement.amount)))} a partes iguales: ${reparto
                    .map((id, indice) => {
                      const piso = pisosReparto.find((p) => p.id === id);
                      return `${piso?.name ?? ''} ${formatEurCents(partesReparto[indice] ?? 0)}`;
                    })
                    .join(' · ')}. Cada piso lleva su parte como gasto del mes.`
                : 'Marca los pisos entre los que se reparte el cargo: dos o más.'
            }
            error={fieldErrors.propertyIds}
          >
            <div className={styles.reparto}>
              {pisosReparto.map((piso) => (
                <Checkbox
                  key={piso.id}
                  label={piso.name}
                  checked={reparto.includes(piso.id)}
                  onChange={(event) => {
                    const marcados = new Set(reparto);
                    if (event.target.checked) marcados.add(piso.id);
                    else marcados.delete(piso.id);
                    // En el orden de la lista: los céntimos que sobran van a los primeros.
                    setReparto(pisosReparto.filter((p) => marcados.has(p.id)).map((p) => p.id));
                  }}
                />
              ))}
            </div>
          </Field>
        )}
        {editaPagador && (
          <Field
            label="Quién paga"
            help={
              fila.pagador?.sugerido && pagador === fila.pagador.id
                ? `Propuesto: ${fila.pagador.motivo ?? 'el más probable'}. Se guarda al guardar, y también en los ingresos exactamente iguales (mismo concepto y mismo importe).`
                : 'Al guardar se pone también en los ingresos exactamente iguales (mismo concepto y mismo importe) y se recuerda para los próximos extractos.'
            }
            error={fieldErrors.tenantId}
          >
            <Select
              options={tenants.map((t) => ({ value: t.id, label: `${t.fullName} · ${t.label}` }))}
              placeholder="Sin decir"
              value={pagador}
              onChange={(event) => {
                const elegido = tenants.find((t) => t.id === event.target.value);
                setPagador(event.target.value);
                // Al elegir quién paga, su inmueble (y su habitación, en la pestañita de al lado).
                if (elegido?.propertyId) setInmueble(elegido.propertyId);
              }}
            />
          </Field>
        )}
        {editaNotas && (
          <Field label="Observaciones" optional error={fieldErrors.notes ?? fieldErrors.reference}>
            <ConVaciar valor={notes} onVaciar={() => setNotes('')} etiqueta="Vaciar las observaciones" arriba>
              <Textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={3}
                maxLength={payment && !entry && !movement ? 200 : 500}
                placeholder={!notes && fila.observaciones ? fila.observaciones : 'Escribe una nota…'}
              />
            </ConVaciar>
          </Field>
        )}
      </FormSection>

      {/* --- Soporte documental --------------------------------------------------------- */}
      <FormSection title="Soporte documental">
        {documento ? (
          <div className={styles.doc}>
            <Icon name="file-lines" size={18} strokeWidth={1.8} />
            <button
              type="button"
              className={styles.docNombre}
              onClick={() => visor.open({ id: documento.id, name: documento.name })}
              title={`Ver ${documento.name}`}
            >
              {documento.name}
            </button>
            <button
              type="button"
              className={styles.docAccion}
              onClick={() => visor.open({ id: documento.id, name: documento.name })}
              aria-label={`Ver ${documento.name}`}
              title="Ver"
            >
              <Icon name="eye" size={15} strokeWidth={2} />
            </button>
          </div>
        ) : (
          <p className={styles.nota}>
            {fila.soporte === 'extracto'
              ? 'Lo justifica el extracto del banco. Puedes añadir además un justificante.'
              : 'Esta fila no tiene ningún documento todavía.'}
          </p>
        )}
        <input
          ref={ficheroRef}
          type="file"
          accept="application/pdf,image/*"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) subir.mutate(file);
          }}
        />
        <div className={styles.docBotones}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => ficheroRef.current?.click()}
            disabled={ocupado}
          >
            <Icon name="upload" size={14} strokeWidth={2.2} />
            {subir.isPending ? 'Subiendo…' : documento ? 'Cambiar documento' : 'Subir documento'}
          </Button>
          {documento && (
            <Button variant="secondary" size="sm" onClick={() => quitar.mutate()} disabled={ocupado}>
              <Icon name="trash" size={14} strokeWidth={2} />
              {quitar.isPending ? 'Quitando…' : 'Quitar documento'}
            </Button>
          )}
        </div>
        {cargoPendiente && !inmueble && (
          <p className={styles.nota}>
            Sin inmueble elegido, el del gasto lo saco del nombre del fichero o de la factura (su CUPS o su dirección).
          </p>
        )}
      </FormSection>

      {/* --- En el banco: cómo está resuelto, y el panel para cambiarlo ------------------ */}
      {movement && onResolve && !entry && (
        <FormSection title="En el banco">
          <p className={styles.nota}>{ESTADO_BANCO[movement.status]}.</p>
          <div>
            <Button variant="secondary" size="sm" onClick={() => onResolve(movement)} disabled={ocupado}>
              {movement.status === 'pendiente' ? 'Resolver este movimiento…' : 'Cambiar a quién o a qué corresponde…'}
            </Button>
          </div>
        </FormSection>
      )}
    </Drawer>
  );
}

/** Un dato que no se edita aquí: etiqueta y valor. */
function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div className={styles.dato}>
      <dt>{etiqueta}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** Un campo con su «×» para vaciarlo de un toque. */
function ConVaciar({
  valor,
  onVaciar,
  etiqueta,
  arriba = false,
  children,
}: {
  valor: string;
  onVaciar: () => void;
  etiqueta: string;
  /** En un texto largo, la «×» va arriba a la derecha en vez de centrada. */
  arriba?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={styles.vaciable}>
      {children}
      {valor && (
        <button
          type="button"
          className={arriba ? `${styles.vaciar} ${styles.vaciarArriba}` : styles.vaciar}
          onClick={onVaciar}
          aria-label={etiqueta}
          title="Vaciar"
        >
          <Icon name="close" size={13} strokeWidth={2.4} />
        </button>
      )}
    </div>
  );
}

/** Lo que hará «Guardar» con la propuesta de la IA, en una frase. */
function queHaraLaIa(
  ia: NonNullable<BankMovement['aiProposal']>,
  tenants: { id: string; fullName: string }[],
  properties: { id: string; name: string }[],
): string {
  const inmueble = properties.find((p) => p.id === ia.propertyId)?.name ?? 'el inmueble que propone';
  switch (ia.action) {
    case 'tenant':
      return `es de ${tenants.find((t) => t.id === ia.tenantId)?.fullName ?? 'quien propone'} (y lo mismo en los ingresos iguales)`;
    case 'property':
      return `gasto de ${inmueble}${ia.concept ? `: ${ia.concept}` : ''}`;
    case 'recurring':
      return `el gasto fijo de ${inmueble}${ia.concept ? `: ${ia.concept}` : ''}`;
    case 'general':
      return `gasto general${ia.concept ? `: ${ia.concept}` : ''}`;
    case 'link':
      return 'paga el gasto apuntado que propone';
    case 'ignore':
      return 'no es de ningún inmueble, se ignora';
  }
}
